import React from 'react'
import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'
import { useQuery } from '@apollo/client'
import { Container } from '@scentregroup/ui'
import classNames from 'classnames'
import NationalHeader from '../components/national-header'
import UniversalHeader, { useCart } from '../components/universal-header'
import useUser from '../components/universal-header/use-user'
import { CentreSelector as NationalCentreSelector } from '../components/national-centre-selector'
import CentreSelector from '../components/legacy-centre-selector'
import withNationalExperienceNavLinks from '../containers/with-national-experience-nav-links'
import { MarketplaceBillboard } from '../components/marketplace-billboard'
import Promotion from '../components/promotion'
import { MarketplaceAngleContainer } from '../components/marketplace-angle-container'
import { MarketplaceContainer } from '../components/marketplace-container'
import { UniversalFooter } from '../components/universal-footer'
import NationalFooter from '../components/national-footer'
import { FeaturedLinksHero } from '../components/featured-links-hero'
import withNationalHomepage from '../containers/with-national-homepage'
import withNationalNavLinks from '../containers/with-national-nav-links'
import withCentreSelector from '../containers/with-centre-selector'
import withGiftCards from '../containers/with-gift-cards'
import { promotionType } from '../types'
import analyticsUtils from '../helpers/analytics-utils'
import { marketplaceRetailerLinkToFeaturedLink } from '../helpers/marketplace-retailer-link-to-featured-link.ts'
import ContentGroup from '../components/content-group'
import { FeaturedLinksGrid } from '../components/featured-links-grid'
import { useCountryContext, AgentInfoContext, LinkContext } from '../context'
import {
  CONFIG_KEYS,
  getAlgoliaApiKeys,
  getClientConfigValue,
} from '../helpers/config-helper'
import pagesHoc from './pages-hoc'
import style from './index.scss'

const identityClientUrl = getClientConfigValue(
  CONFIG_KEYS.NEXT_PUBLIC_IDENTITY_CLIENT_URL
)

const Index = ({
  centres,
  giftCards,
  nationalExperienceNavLinks,
  nationalNavLinks,
  homePageInfo,
  isEvolved,
  promotion,
  features,
  retailers,
  featuredCategoriesCollection,
}) => {
  const country = useCountryContext()
  const cart = useCart(isEvolved ? useQuery : undefined)
  const { user } = useUser(isEvolved ? useQuery : undefined)

  return isEvolved ? (
    <Container>
      <UniversalHeader
        Link={Link}
        country={country}
        centres={centres}
        giftCards={giftCards}
        cart={{
          url: '/shop/cart2',
          count: cart?.marketplaceItems?.length,
        }}
        user={user}
        pinnedLinks={{ items: nationalExperienceNavLinks }}
        account={{
          baseUrl: identityClientUrl,
          from: isEvolved ? '' : 'westfield',
        }}
        nationalNav={nationalNavLinks}
        algoliaApiKeys={getAlgoliaApiKeys()}
      />
      <LinkContext.Provider value={Link}>
        <NationalCentreSelector centres={centres} />
      </LinkContext.Provider>
      <FeaturedLinksHero featuredLinks={features} Link={Link} />
      <MarketplaceBillboard Link={Link} />
      <MarketplaceAngleContainer>
        <FeaturedLinksGrid
          Link={Link}
          featuredLinks={retailers.map(marketplaceRetailerLinkToFeaturedLink)}
          itemsPerRow={4}
          title="Shop stores"
        />
      </MarketplaceAngleContainer>
      <MarketplaceContainer>
        <FeaturedLinksGrid
          Link={Link}
          featuredLinks={featuredCategoriesCollection}
          itemsPerRow={3}
          title="Shop by category"
        />
      </MarketplaceContainer>
      <div style={{ backgroundColor: '#f5f6f7', padding: '84px 0' }}>
        {promotion && (
          <Promotion
            {...promotion}
            eventCategory={analyticsUtils.categories.GIFTCARD_CLICK}
            eventAction={analyticsUtils.actions.GIFTCARD_NAT_HOME}
          />
        )}
      </div>
      <UniversalFooter />
    </Container>
  ) : (
    <Container className={classNames(style.homeBody)}>
      <AgentInfoContext.Consumer>
        {({ isMobile }) => (
          <>
            <NationalHeader promotionalUrl={promotion.callToActionAddress} />
            <CentreSelector
              centres={centres}
              homePageInfo={homePageInfo}
              isNZ={country !== 'Australia'}
              isMobile={isMobile}
            />
          </>
        )}
      </AgentInfoContext.Consumer>
      <ContentGroup angleOrigin="top-left" color="Grey" key="0">
        {promotion && (
          <Promotion
            {...promotion}
            eventCategory={analyticsUtils.categories.GIFTCARD_CLICK}
            eventAction={analyticsUtils.actions.GIFTCARD_NAT_HOME}
          />
        )}
      </ContentGroup>
      <NationalFooter />
    </Container>
  )
}

Index.propTypes = {
  centres: PropTypes.arrayOf(PropTypes.object),
  giftCards: PropTypes.object,
  nationalExperienceNavLinks: PropTypes.arrayOf(PropTypes.object),
  nationalNavLinks: PropTypes.object,
  homePageInfo: PropTypes.object,
  promotion: promotionType,
  isEvolved: PropTypes.bool,
  features: PropTypes.arrayOf(PropTypes.object),
  retailers: PropTypes.arrayOf(PropTypes.object),
  featuredCategoriesCollection: PropTypes.arrayOf(PropTypes.object),
}

Index.defaultProps = {
  centres: [],
  homePageInfo: {},
  isEvolved: false,
}

const getHomepageStructuredData = ({ url }) =>
  url
    ? {
        '@context': 'http://schema.org',
        '@type': 'WebSite',
        url,
        potentialAction: {
          '@type': 'SearchAction',
          target: `${url}search?q={search_term_string}`,
          'query-input': 'required name=search_term_string',
        },
      }
    : null

const getMetaData = ({ centres, canonical }) => ({
  title: `Westfield ${centres[0].country} - Visit Our Centres or Browse Online`,
  description:
    'Westfield is your one-stop destination for shopping, leisure and entertainment. Discover 1000s of popular retailers in fashion, home, decor and more.',
  schema: getHomepageStructuredData({ url: canonical }),
})

export default withCentreSelector(
  withGiftCards(
    withNationalExperienceNavLinks(
      withNationalHomepage(withNationalNavLinks(pagesHoc(Index, getMetaData)))
    )
  )
)
